import { Grid } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDatePicker,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroInput,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
	CerebroYesNo,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import {
	BaseRateTypes,
	BaseRateTypesMap,
	DepositRefundTypes,
	DepositRefundTypesMap,
	FacilityAmortizationPeriods,
	FacilityAmortizationPeriodsMap,
	FacilityAmortizationStructures,
	FacilityAmortizationStructuresMap,
	FacilityFactorTypes,
	FacilityFactorTypesMap,
	FacilityTypeMap,
	FacilityTypes,
	InterestOnlyPeriodDurations,
	InterestOnlyPeriodDurationsMap,
	InterestRateTypes,
	InterestRateTypesMap,
	RateTypes,
	RateTypesMap,
	StructureTrancheMap,
	StructureTranches,
	TermSheetFacility,
} from '../../../shared/models';
import { AxiosUtils, DateUtils, FormikUtils, NumberUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';
import { isEither, isLeaseFacilityType, isTermLoan } from '../../../shared/utils/entity';
import { MaxCurrencyLength } from '../../../shared/utils/platform';
import { validateDateWithinPlusMinus100Years } from '../../../shared/utils/date';

interface FacilityFormProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	facility: TermSheetFacility | null;
	close: () => void;
}

export const FacilityForm: React.FC<FacilityFormProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	facility,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onGetBankRateValue = async (
		value: BaseRateTypes | undefined,
		setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
	) => {
		const rateValue = await AxiosUtils.getResponseData<number | undefined>(
			api.get(CerebroApiEndpoints.TermSheets.HistoricalBankRate(revisionGuid, value))
		);

		setFieldValue('baseRateValue', NumberUtils.FormatFloat(rateValue, 2));
	};

	const calculateMonthlyPayment = (values: FacilityFormValues): number => {
		const highEndAmount = NumberUtils.ParseDecimal(values.highEndAmount, undefined);
		const leaseRateFactor =
			values.facilityFactorType == 'PaymentAmount'
				? undefined
				: NumberUtils.ParseDecimal(values.leaseRateFactor, undefined);

		return (leaseRateFactor ?? 0.0) * (highEndAmount ?? 0.0);
	};

	const calculateInterestRate = (values: FacilityFormValues): number => {
		const highEndAmount = NumberUtils.ParseDecimal(values.highEndAmount) ?? 0.0;
		const highEndMaturity = NumberUtils.ParseDecimal(values.termMonths) ?? 0.0;
		const leaseRateFactor =
			values.facilityFactorType == 'PaymentAmount'
				? 0.0
				: NumberUtils.ParseDecimal(values.leaseRateFactor) ?? 0.0;
		const leaseResidual = NumberUtils.ParseDecimal(values.leaseResidual) ?? 0.0;
		const maturityDenominator = highEndMaturity > 0 ? highEndMaturity / 12 : 0.0;
		const denominator = maturityDenominator != 0 ? (highEndAmount - leaseResidual) / 2 / maturityDenominator : 0.0;
		const interestRate = denominator != 0 ? (leaseRateFactor * highEndAmount * highEndMaturity) / denominator : 0.0;

		return interestRate;
	};

	const facilityMutation = useMutation(
		async ({ values }: FacilityFormMutationVariables) => {
			const leaseRateFactor =
				values.facilityFactorType == 'PaymentAmount'
					? undefined
					: NumberUtils.ParseDecimal(values.leaseRateFactor);
			const leasePaymentAmount =
				values.facilityFactorType == 'PaymentAmount'
					? NumberUtils.ParseDecimal(values.leasePaymentAmount)
					: undefined;

			const patchValues = {
				facilityType: values.facilityType,
				structureTranche: values.structureTranche,
				highEndAmount: NumberUtils.ParseDecimal(values.highEndAmount),
				accordionAmount: NumberUtils.ParseDecimal(values.accordionAmount),
				termMonths: NumberUtils.ParseDecimal(values.termMonths),
				ioPeriod: NumberUtils.ParseDecimal(values.ioPeriod),
				amortizationStructure: values.amortizationStructure,
				facilityAmortizationPeriod: values.facilityAmortizationPeriod,
				ecfSweep: values.ecfSweep,
				leaseRateFactor: leaseRateFactor,
				leaseResidual: NumberUtils.ParseDecimal(values.leaseResidual),
				exerciseLeaseResidual: values.exerciseLeaseResidual,
				rateType: values.rateType,
				interestCash: NumberUtils.ParseDecimal(values.interestCash),
				interestRateType: values.interestRateType,
				baseRate: values.baseRate,
				baseRateFloor: NumberUtils.ParseDecimal(values.baseRateFloor),
				baseRateValue: NumberUtils.ParseDecimal(values.baseRateValue),
				pikInterest: NumberUtils.ParseDecimal(values.pikInterest),
				pikInterestPeriod: values.pikInterestPeriod,
				pikInterestCompounded: values.pikInterestCompounded,
				requiresDeposit: values.requiresDeposit,
				depositAmount: NumberUtils.ParseDecimal(values.depositAmount),
				depositRefundType: values.depositRefundType,
				facilityFactorType: values.facilityFactorType,
				leasePaymentAmount: leasePaymentAmount,
				isInterestOnlyPeriod: values.isInterestOnlyPeriod,
				interestOnlyPeriodDuration: values.interestOnlyPeriodDuration,
				otherBaseRateName: values.otherBaseRateName,
				otherBaseRateValue: NumberUtils.ParseDecimal(values.otherBaseRateValue),
				feesProvided: values.feesProvided,
				pricingComments: values.pricingComments,
				hasGracePeriod: values.hasGracePeriod,
				gracePeriodLength: NumberUtils.ParseDecimal(values.gracePeriodLength),
				gracePeriodLengthType: values.gracePeriodLengthType,
				amortizationPeriodLength: NumberUtils.ParseDecimal(values.amortizationPeriodLength),
				isDemandLine: values.isDemandLine,
				maturityDate: values.maturityDate ? values.maturityDate.toISOString().split('T')[0] : null
			};

			if (facility != null && facility.id == 0) {
				const { data, error } = await getApiResponseData<void>(
					api.post(
						CerebroApiEndpoints.TermSheets.Facility(dataRoomGuid, termSheetGuid, revisionGuid),
						patchValues
					)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			} else if (facility != null && facility.id > 0) {
				const { data, error } = await getApiResponseData<void>(
					api.patch(
						CerebroApiEndpoints.TermSheets.FacilitySpecific(
							dataRoomGuid,
							termSheetGuid,
							revisionGuid,
							facility?.guid
						),
						patchValues
					)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}

			return null;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid));
				snackbar.enqueueSnackbar('Term sheet facility was updated.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<Formik
			initialValues={{
				facilityType: facility?.facilityType,
				structureTranche: facility?.structureTranche,
				highEndAmount: NumberUtils.FormatDecimal(facility?.highEndAmount),
				accordionAmount: NumberUtils.FormatDecimal(facility?.accordionAmount),
				termMonths: NumberUtils.FormatNumber(facility?.termMonths) ?? '',
				ioPeriod: NumberUtils.FormatNumber(facility?.ioPeriod),
				amortizationStructure: facility?.amortizationStructure,
				facilityAmortizationPeriod: facility?.facilityAmortizationPeriod,
				ecfSweep: facility?.ecfSweep,
				leaseRateFactor: NumberUtils.FormatFloat(facility?.leaseRateFactor, 7),
				leaseResidual: NumberUtils.FormatDecimal(facility?.leaseResidual),
				exerciseLeaseResidual: facility?.exerciseLeaseResidual,
				rateType: facility?.rateType ?? 'Percentage',
				interestCash: NumberUtils.FormatFloat(facility?.interestCash, 2),
				interestRateType: facility?.interestRateType,
				baseRate: facility?.baseRate,
				baseRateFloor: NumberUtils.FormatFloat(facility?.baseRateFloor, 2),
				baseRateValue: NumberUtils.FormatFloat(facility?.baseRateValue, 2),
				pikInterest: NumberUtils.FormatFloat(facility?.pikInterest, 2),
				pikInterestPeriod: facility?.pikInterestPeriod,
				pikInterestCompounded: facility?.pikInterestCompounded,
				requiresDeposit: facility?.requiresDeposit,
				depositAmount: NumberUtils.FormatDecimal(facility?.depositAmount),
				depositRefundType: facility?.depositRefundType,
				facilityFactorType: facility?.facilityFactorType ?? 'RateFactor',
				leasePaymentAmount: NumberUtils.FormatDecimal(facility?.leasePaymentAmount),
				isInterestOnlyPeriod: facility?.isInterestOnlyPeriod ?? false,
				interestOnlyPeriodDuration: facility?.interestOnlyPeriodDuration ?? 'Months',
				otherBaseRateName: facility?.otherBaseRateName ?? '',
				otherBaseRateValue: NumberUtils.FormatFloat(facility?.otherBaseRateValue, 4),
				feesProvided: facility?.feesProvided ?? true,
				pricingComments: facility?.pricingComments ?? '',
				hasGracePeriod: facility?.hasGracePeriod ?? false,
				gracePeriodLength: NumberUtils.FormatNumber(facility?.gracePeriodLength),
				gracePeriodLengthType: facility?.gracePeriodLengthType,
				amortizationPeriodLength: NumberUtils.FormatNumber(facility?.amortizationPeriodLength),
				isDemandLine: facility?.isDemandLine ?? false,
				maturityDate: DateUtils.ParseDateOnly(facility?.maturityDate),
			}}
			validate={validateForm}
			onSubmit={FormikUtils.MutateOnSubmit(facilityMutation.mutate)}
		>
			{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
				const showRateFactor =
					values.facilityFactorType == 'RateFactor' || values.facilityFactorType == 'NotApplicable';

				const monthlyPayment = calculateMonthlyPayment(values);
				const interestRate = calculateInterestRate(values);

				return (
					<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ marginTop: 3, paddingRight: 3 }}>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={11}>
								<CerebroLabel id="facilityType" required>
									What best describes the loan type?
								</CerebroLabel>
								<Field
									id="facilityType"
									as={CerebroDropdown}
									name="facilityType"
									disabled={isSubmitting}
								>
									{Object.entries(FacilityTypeMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="structureTranche" required>
									What best describes the loan structure?
								</CerebroLabel>
								<Field
									id="structureTranche"
									as={CerebroDropdown}
									name="structureTranche"
									disabled={isSubmitting}
								>
									{Object.entries(StructureTrancheMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="highEndAmount" required>
									What is the commitment amount?
								</CerebroLabel>
								<Field
									formatas="currency"
									as={CerebroMaskedInput}
									maxLength={MaxCurrencyLength}
									id="highEndAmount"
									name="highEndAmount"
									disabled={isSubmitting}
									error={touched.highEndAmount && errors.highEndAmount !== undefined}
								/>
								{errors.highEndAmount && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.highEndAmount}
									</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="accordionAmount">What is the accordion amount?</CerebroLabel>
								<Field
									formatas="currency"
									as={CerebroMaskedInput}
									maxLength={MaxCurrencyLength}
									id="accordionAmount"
									name="accordionAmount"
									disabled={isSubmitting}
									error={touched.accordionAmount && errors.accordionAmount !== undefined}
								/>
								{errors.accordionAmount && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.accordionAmount}
									</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="termMonths" required>
									What is the term/maturity (in months)?
								</CerebroLabel>
								<Field
									as={CerebroMaskedInput}
									allowDecimals={false}
									id="termMonths"
									name="termMonths"
									disabled={isSubmitting}
									error={touched.termMonths && errors.termMonths !== undefined}
								/>
								{errors.termMonths && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.termMonths}
									</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="maturityDate">Maturity Date</CerebroLabel>
								<Field
									id="maturityDate"
									name="maturityDate"
									component={CerebroDatePicker}
									disabled={isSubmitting}
									error={touched.maturityDate && errors.maturityDate !== undefined}
								/>
								{touched.maturityDate && errors.maturityDate && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.maturityDate}
									</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="isDemandLine">Is this a Demand Line?</CerebroLabel>
								<Field
									as={CerebroYesNo}
									id="isDemandLine"
									name="isDemandLine"
									disabled={isSubmitting}
								/>
							</Grid>

							{isTermLoan(values.facilityType) && (
								<>
									<Grid item xs={11}>
										<CerebroLabel id="amortizationStructure">
											What is the amortization structure?
										</CerebroLabel>
										<Field
											id="amortizationStructure"
											as={CerebroDropdown}
											name="amortizationStructure"
											disabled={isSubmitting}
										>
											{Object.entries(FacilityAmortizationStructuresMap).map(
												([value, description]) => (
													<CerebroDropdownMenuItem key={value} value={value}>
														{description}
													</CerebroDropdownMenuItem>
												)
											)}
										</Field>
									</Grid>
									{values.amortizationStructure != 'None' && (
										<>
											<Grid item xs={11}>
												<CerebroLabel id="facilityAmortizationPeriod">
													Amortization periods?
												</CerebroLabel>
												<Field
													id="facilityAmortizationPeriod"
													as={CerebroDropdown}
													name="facilityAmortizationPeriod"
													disabled={isSubmitting}
												>
													{Object.entries(FacilityAmortizationPeriodsMap).map(
														([value, description]) => (
															<CerebroDropdownMenuItem key={value} value={value}>
																{description}
															</CerebroDropdownMenuItem>
														)
													)}
												</Field>
											</Grid>
											<Grid item xs={6}>
												<CerebroLabel id="amortizationPeriodLength">
													Amortization Length (# months)
												</CerebroLabel>
												<Field
													as={CerebroMaskedInput}
													id="amortizationPeriodLength"
													name="amortizationPeriodLength"
													disabled={isSubmitting}
												/>
											</Grid>
										</>
									)}
									<Grid item xs={11}>
										<CerebroLabel id="hasGracePeriod">Include a Grace Period?</CerebroLabel>
										<Field
											as={CerebroYesNo}
											id="hasGracePeriod"
											name="hasGracePeriod"
											disabled={isSubmitting}
										/>
									</Grid>
									{values.hasGracePeriod && (
										<>
											<Grid item xs={6}>
												<CerebroLabel id="gracePeriodLength">Period Length</CerebroLabel>
												<Field
													as={CerebroMaskedInput}
													id="gracePeriodLength"
													name="gracePeriodLength"
													disabled={isSubmitting}
												/>
											</Grid>
											<Grid item xs={5}>
												<CerebroLabel id="gracePeriodLengthType">&nbsp;</CerebroLabel>
												<Field
													id="gracePeriodLengthType"
													as={CerebroDropdown}
													name="gracePeriodLengthType"
													disabled={isSubmitting}
												>
													{Object.entries(InterestOnlyPeriodDurationsMap).map(
														([value, description]) => (
															<CerebroDropdownMenuItem key={value} value={value}>
																{description}
															</CerebroDropdownMenuItem>
														)
													)}
												</Field>
											</Grid>
										</>
									)}
									<Grid item xs={11}>
										<CerebroLabel id="isInterestOnlyPeriod">
											Include an Interest Only Period?
										</CerebroLabel>
										<Field
											as={CerebroYesNo}
											id="isInterestOnlyPeriod"
											name="isInterestOnlyPeriod"
											disabled={isSubmitting}
										/>
									</Grid>
									{values.isInterestOnlyPeriod && (
										<>
											<Grid item xs={6}>
												<CerebroLabel id="ioPeriod">Period Length</CerebroLabel>
												<Field
													as={CerebroMaskedInput}
													id="ioPeriod"
													name="ioPeriod"
													disabled={isSubmitting}
												/>
											</Grid>
											<Grid item xs={5}>
												<CerebroLabel id="interestOnlyPeriodDuration">&nbsp;</CerebroLabel>
												<Field
													id="interestOnlyPeriodDuration"
													as={CerebroDropdown}
													name="interestOnlyPeriodDuration"
													disabled={isSubmitting}
												>
													{Object.entries(InterestOnlyPeriodDurationsMap).map(
														([value, description]) => (
															<CerebroDropdownMenuItem key={value} value={value}>
																{description}
															</CerebroDropdownMenuItem>
														)
													)}
												</Field>
											</Grid>
										</>
									)}
									<Grid item xs={11}>
										<CerebroLabel id="ecfSweep">Excess cash flow sweep amount</CerebroLabel>
										<Field
											as={CerebroInput}
											id="ecfSweep"
											name="ecfSweep"
											disabled={isSubmitting}
										/>
									</Grid>
								</>
							)}

							{isLeaseFacilityType(values.facilityType) && (
								<>
									<Grid item xs={11}>
										<CerebroLabel id="facilityFactorType">Rate Factor/Payment Amount?</CerebroLabel>
										<Field
											id="facilityFactorType"
											as={CerebroDropdown}
											name="facilityFactorType"
											disabled={isSubmitting}
										>
											{Object.entries(FacilityFactorTypesMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
									</Grid>
									{showRateFactor && (
										<Grid item xs={11}>
											<CerebroLabel id="leaseRateFactor">
												What is the lease rate factor for the facility?
											</CerebroLabel>
											<Field
												as={CerebroMaskedInput}
												allowDecimals={true}
												minDecimalPlaces={2}
												maxDecimalPlaces={7}
												id="leaseRateFactor"
												name="leaseRateFactor"
												placeholder={'Enter as decimal value, e.g., 0.02355 and not 2.355%'}
												disabled={isSubmitting}
												error={touched.leaseRateFactor && errors.leaseRateFactor !== undefined}
											/>
											<CerebroText>
												Monthly Payment: {NumberUtils.FormatCurrency(monthlyPayment)}
											</CerebroText>
											<CerebroText>
												Effective Interest Rate: {interestRate.toFixed(2)}%
											</CerebroText>
											{errors.leaseRateFactor && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.leaseRateFactor}
												</CerebroText>
											)}
										</Grid>
									)}
									{values.facilityFactorType == 'PaymentAmount' && (
										<Grid item xs={11}>
											<CerebroLabel id="leasePaymentAmount">
												What is the lease payment amount?
											</CerebroLabel>
											<Field
												as={CerebroMaskedInput}
												id="leasePaymentAmount"
												name="leasePaymentAmount"
												disabled={isSubmitting}
												error={
													touched.leasePaymentAmount &&
													errors.leasePaymentAmount !== undefined
												}
											/>
											{errors.leasePaymentAmount && (
												<CerebroText variant="error" sx={{ marginBottom: 4 }}>
													{errors.leasePaymentAmount}
												</CerebroText>
											)}
										</Grid>
									)}
									<Grid item xs={11}>
										<CerebroLabel id="leaseResidual">
											What is the residual value for lease buyout?
										</CerebroLabel>
										<Field
											as={CerebroMaskedInput}
											id="leaseResidual"
											name="leaseResidual"
											disabled={isSubmitting}
											error={touched.leaseResidual && errors.leaseResidual !== undefined}
										/>
										{errors.leaseResidual && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.leaseResidual}
											</CerebroText>
										)}
									</Grid>
									<Grid item xs={11}>
										<CerebroLabel id="exerciseLeaseResidual">
											Will Borrower exercise residual?
										</CerebroLabel>
										<Field
											as={CerebroYesNo}
											id="exerciseLeaseResidual"
											name="exerciseLeaseResidual"
											disabled={isSubmitting}
										/>
									</Grid>
								</>
							)}

							{isEither(values.facilityType) && (
								<>
									<Grid item xs={11}>
										<CerebroLabel required>Interest Rate Type</CerebroLabel>
										<Field
											id="interestRateType"
											as={CerebroDropdown}
											name="interestRateType"
											disabled={isSubmitting}
											error={touched.interestRateType && errors.interestRateType !== undefined}
										>
											{Object.entries(InterestRateTypesMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
										{errors.interestRateType && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.interestRateType}
											</CerebroText>
										)}
									</Grid>
									<Grid item xs={7}>
										<CerebroLabel id="interestCash" required>
											Annual Cash Interest
										</CerebroLabel>
										<Field
											as={CerebroMaskedInput}
											allowDecimals={true}
											minDecimalPlaces={1}
											maxDecimalPlaces={2}
											id="interestCash"
											name="interestCash"
											disabled={isSubmitting}
											error={touched.interestCash && errors.interestCash !== undefined}
										/>
										{errors.interestCash && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.interestCash}
											</CerebroText>
										)}
									</Grid>
									<Grid item xs={4}>
										<CerebroLabel id="rateType">&nbsp;</CerebroLabel>
										<Field
											id="rateType"
											as={CerebroDropdown}
											name="rateType"
											disabled={isSubmitting}
										>
											{Object.entries(RateTypesMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
									</Grid>
								</>
							)}

							{values.interestRateType == 'Float' && !isLeaseFacilityType(values.facilityType) && (
								<>
									<Grid item xs={7}>
										<CerebroLabel id="baseRate" required>
											Base Type
										</CerebroLabel>
										<Field
											id="baseRate"
											as={CerebroDropdown}
											name="baseRate"
											disabled={isSubmitting}
											error={touched.baseRate && errors.baseRate !== undefined}
											onChange={(e: any) => {
												setFieldValue('baseRate', e.target.value);
												onGetBankRateValue(e.target.value as BaseRateTypes, setFieldValue);
											}}
										>
											{Object.entries(BaseRateTypesMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
										{errors.baseRate && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.baseRate}
											</CerebroText>
										)}
									</Grid>
									<Grid item xs={4}>
										<CerebroText>&nbsp;</CerebroText>
										{values.baseRate != 'Other' && (
											<CerebroText sx={{ marginTop: 3 }}>{values.baseRateValue}%</CerebroText>
										)}
									</Grid>
									{values.baseRate == 'Other' && (
										<>
											<Grid item xs={7}>
												<CerebroLabel id="otherBaseRateName">
													Name of reference base rate
												</CerebroLabel>
												<Field
													as={CerebroInput}
													id="otherBaseRateName"
													name="otherBaseRateName"
													disabled={isSubmitting}
												/>
											</Grid>
											<Grid item xs={5}>
												<CerebroLabel id="otherBaseRateValue">Current Value</CerebroLabel>
												<Field
													as={CerebroMaskedInput}
													allowDecimals={true}
													minDecimalPlaces={1}
													maxDecimalPlaces={4}
													id="otherBaseRateValue"
													name="otherBaseRateValue"
													placeholder="enter as a percent (i.e. 3.75 for 3.75%)"
													disabled={isSubmitting}
												/>
											</Grid>
										</>
									)}
									<Grid item xs={11}>
										<CerebroLabel id="baseRateFloor">Base Rate Floor</CerebroLabel>
										<Field
											as={CerebroMaskedInput}
											allowDecimals={true}
											minDecimalPlaces={1}
											maxDecimalPlaces={2}
											id="baseRateFloor"
											name="baseRateFloor"
											disabled={isSubmitting}
										/>
									</Grid>
								</>
							)}

							{isTermLoan(values.facilityType) && (
								<>
									<Grid item xs={11}>
										<CerebroLabel id="pikInterest">PIK Interest Rate</CerebroLabel>
										<Field
											as={CerebroMaskedInput}
											id="pikInterest"
											name="pikInterest"
											minDecimalPlaces={2}
											maxDecimalPlaces={2}
											allowDecimals={true}
											disabled={isSubmitting}
										/>
									</Grid>
									<Grid item xs={11}>
										<CerebroLabel id="pikInterestPeriod">PIK Interest Period</CerebroLabel>
										<Field
											id="pikInterestPeriod"
											as={CerebroDropdown}
											name="pikInterestPeriod"
											disabled={isSubmitting}
										>
											{Object.entries(FacilityAmortizationPeriodsMap).map(
												([value, description]) => (
													<CerebroDropdownMenuItem key={value} value={value}>
														{description}
													</CerebroDropdownMenuItem>
												)
											)}
										</Field>
									</Grid>
									<Grid item xs={11}>
										<CerebroLabel id="pikInterestCompounded">PIK Interest Compounded</CerebroLabel>
										<Field
											as={CerebroYesNo}
											id="pikInterestCompounded"
											name="pikInterestCompounded"
											disabled={isSubmitting}
										/>
									</Grid>
								</>
							)}

							<Grid item xs={11}>
								<CerebroLabel id="pricingComments">
									Enter any relevant or non-standard pricing information
								</CerebroLabel>
								<Field
									as={CerebroTextarea}
									id="pricingComments"
									name="pricingComments"
									rows="4"
									disabled={isSubmitting}
								/>
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="requiresDeposit">Is a good faith deposit required?</CerebroLabel>
								<Field
									as={CerebroYesNo}
									id="requiresDeposit"
									name="requiresDeposit"
									disabled={isSubmitting}
								/>
							</Grid>
							{values.requiresDeposit == true && (
								<>
									<Grid item xs={5}>
										<CerebroLabel id="depositAmount">How much is required?</CerebroLabel>
									</Grid>
									<Grid item xs={7}>
										<CerebroLabel id="depositRefundType">Refundable?</CerebroLabel>
									</Grid>
									<Grid item xs={5}>
										<Field
											formatas="currency"
											as={CerebroMaskedInput}
											maxLength={MaxCurrencyLength}
											id="depositAmount"
											name="depositAmount"
											disabled={isSubmitting}
											error={touched.depositAmount && errors.depositAmount !== undefined}
										/>
										{errors.depositAmount && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.depositAmount}
											</CerebroText>
										)}
									</Grid>
									<Grid item xs={6}>
										<Field
											id="depositRefundType"
											as={CerebroDropdown}
											name="depositRefundType"
											disabled={isSubmitting}
										>
											{Object.entries(DepositRefundTypesMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
									</Grid>
								</>
							)}
							<Grid item xs={11}>
								<CerebroLabel id="feesProvided">
									Are there fees associated with this facility?
								</CerebroLabel>
								<Field
									as={CerebroYesNo}
									id="feesProvided"
									name="feesProvided"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={8}>
								<CerebroPrimaryButton loading={isSubmitting} type="submit">
									Save
								</CerebroPrimaryButton>
							</Grid>
						</Grid>
					</CerebroForm>
				);
			}}
		</Formik>
	);
};

type FacilityFormMutationVariables = FormikUtils.MutateOnSubmitVariables<FacilityFormValues>;
interface FacilityFormValues {
	facilityType: FacilityTypes | undefined;
	structureTranche: StructureTranches | undefined;
	highEndAmount: string;
	accordionAmount: string;
	termMonths: string;
	ioPeriod: string | null;
	amortizationStructure: FacilityAmortizationStructures | undefined;
	facilityAmortizationPeriod: FacilityAmortizationPeriods | undefined;
	ecfSweep: string | undefined;
	leaseRateFactor: string;
	leaseResidual: string;
	exerciseLeaseResidual: boolean | undefined;
	rateType: RateTypes;
	interestCash: string;
	interestRateType: InterestRateTypes | undefined;
	baseRate: BaseRateTypes | undefined;
	baseRateFloor: string;
	baseRateValue: string;
	pikInterest: string;
	pikInterestPeriod: FacilityAmortizationPeriods | undefined;
	pikInterestCompounded: boolean | undefined;
	requiresDeposit: boolean | undefined;
	depositAmount: string;
	depositRefundType: DepositRefundTypes | undefined;
	facilityFactorType: FacilityFactorTypes;
	leasePaymentAmount: string;
	isInterestOnlyPeriod: boolean;
	interestOnlyPeriodDuration: InterestOnlyPeriodDurations;
	otherBaseRateName: string;
	otherBaseRateValue: string;
	feesProvided: boolean;
	pricingComments: string;
	hasGracePeriod: boolean;
	gracePeriodLength: string | null;
	gracePeriodLengthType: InterestOnlyPeriodDurations | undefined;
	amortizationPeriodLength: string | null;
	isDemandLine: boolean;
	maturityDate: Date | null;
}
type FacilityFormValueErrors = FormikErrors<FacilityFormValues>;

function validateForm(values: FacilityFormValues) {
	const errors: FacilityFormValueErrors = {};

	const isLease = isLeaseFacilityType(values.facilityType);
	const showRateFactor = values.facilityFactorType == 'RateFactor' || values.facilityFactorType == 'NotApplicable';

	const maturityDateError = validateDateWithinPlusMinus100Years(values.maturityDate, 'Maturity Date');
	if (maturityDateError) {
		errors.maturityDate = maturityDateError;
	}

	if (!isLease) {
		if (!values.interestRateType) {
			errors.interestRateType = 'Interest rate type is required.';
		}

		if (!values.interestCash) {
			errors.interestCash = 'Annual cash interest is required.';
		}
	}

	if (isLease) {
		if (showRateFactor && !values.leaseRateFactor) {
			errors.leaseRateFactor = 'Lease factor rate is required.';
		}
		if (values.facilityFactorType == 'PaymentAmount' && !values.leasePaymentAmount) {
			errors.leasePaymentAmount = 'Lease payment amount is required.';
		}
		if (!values.leaseResidual) {
			errors.leaseResidual = 'Lease residual amount is required.';
		}
	}

	if (!values.highEndAmount || values.highEndAmount === '') {
		errors.highEndAmount = 'Enter a valid commitment amount.';
	}

	const termCheck = NumberUtils.ParseDecimal(values.termMonths, undefined);
	if (!values.termMonths || values.termMonths === '' || !termCheck) {
		errors.termMonths = 'Enter a valid term/maturity.';
	} else {
		if (termCheck > 1188) {
			errors.termMonths = 'Term/maturity cannot exceed 99 years.';
		}
	}

	if (values.requiresDeposit) {
		if (!values.depositAmount || values.depositAmount === '') {
			errors.depositAmount = 'Enter the deposit amount required.';
		}
	}

	if (values.interestRateType == 'Float' && !isLease) {
		if (!values.baseRate) {
			errors.baseRate = 'Base rate is required.';
		}
	}

	return errors;
}
